import React from "react";
import {
    Box,
    Button,
    Divider,
    Grid,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Typography,
    Paper,
} from "@mui/material";
import {
    BadgeOutlined,
    CalendarTodayOutlined,
    DescriptionOutlined,
    Download,
    PersonOutlined,
    TableChartOutlined,
} from "@mui/icons-material";
import InformationTile from "components/Shared/InformationTile";
import InfoGridSkeleton from "components/Shared/Skeleton/InfoGridSkeleton";
import { localizeDateTimeShort } from "utils/dateFormats";
import { useVendorQuotationExcelDetailContext } from "contexts/VendorQuotationExcelContext";

// ─── Price Grid preview ────────────────────────────────────────────────────────

const PriceGrid = ({ brands = [], locations = [], entries = [] }) => {
    const flatProducts = brands.flatMap((brand) =>
        brand.products.map((p) => ({ brandName: brand.name, productName: p.name })),
    );

    if (!flatProducts.length || !locations.filter(Boolean).length) return null;

    const cellValue = (location, brandName, productName) =>
        entries.find(
            (e) =>
                e.location === location &&
                e.brandName === brandName &&
                e.productName === productName,
        )?.value ?? "";

    return (
        <TableContainer component={Paper} variant="outlined" sx={{ overflowX: "auto" }}>
            <Table size="small">
                <TableHead>
                    {/* Brand row */}
                    <TableRow>
                        <TableCell
                            rowSpan={2}
                            sx={{
                                fontWeight: 700,
                                minWidth: 140,
                                bgcolor: "action.hover",
                                borderRight: "2px solid",
                                borderColor: "divider",
                            }}
                        >
                            Location
                        </TableCell>
                        {brands.map((brand, bIdx) =>
                            brand.products.length > 0 ? (
                                <TableCell
                                    key={bIdx}
                                    colSpan={brand.products.length}
                                    align="center"
                                    sx={{
                                        fontWeight: 700,
                                        bgcolor: "action.hover",
                                        borderLeft: "2px solid",
                                        borderColor: "divider",
                                    }}
                                >
                                    {brand.name}
                                </TableCell>
                            ) : null,
                        )}
                    </TableRow>

                    {/* Product row */}
                    <TableRow>
                        {flatProducts.map((fp, pIdx) => (
                            <TableCell
                                key={pIdx}
                                sx={{
                                    fontSize: "0.7rem",
                                    maxWidth: 130,
                                    whiteSpace: "normal",
                                    wordBreak: "break-word",
                                    bgcolor: "action.hover",
                                    borderLeft:
                                        pIdx === 0 ||
                                            flatProducts[pIdx - 1]?.brandName !== fp.brandName
                                            ? "2px solid"
                                            : undefined,
                                    borderColor: "divider",
                                }}
                            >
                                {fp.productName}
                            </TableCell>
                        ))}
                    </TableRow>
                </TableHead>

                <TableBody>
                    {locations.filter(Boolean).map((location, lIdx) => (
                        <TableRow
                            key={lIdx}
                            sx={{ "&:nth-of-type(even)": { bgcolor: "action.hover" } }}
                        >
                            <TableCell
                                sx={{
                                    fontWeight: 600,
                                    whiteSpace: "nowrap",
                                    borderRight: "2px solid",
                                    borderColor: "divider",
                                }}
                            >
                                {location}
                            </TableCell>
                            {flatProducts.map((fp, pIdx) => (
                                <TableCell
                                    key={pIdx}
                                    sx={{
                                        borderLeft:
                                            pIdx === 0 ||
                                                flatProducts[pIdx - 1]?.brandName !== fp.brandName
                                                ? "2px solid"
                                                : undefined,
                                        borderColor: "divider",
                                    }}
                                >
                                    {cellValue(location, fp.brandName, fp.productName) || "—"}
                                </TableCell>
                            ))}
                        </TableRow>
                    ))}
                </TableBody>
            </Table>
        </TableContainer>
    );
};

// ─── Main component ────────────────────────────────────────────────────────────

const VendorQuotationExcelBasicDetail = () => {
    const { priceListDetail, loadingPriceList } = useVendorQuotationExcelDetailContext();

    if (loadingPriceList) return <InfoGridSkeleton />;

    const d = priceListDetail;

    const handleDownload = async () => {
        try {
            const filename = `${d.VendorQuotationExcelId} - ${d.title}.xlsx`;
            const proxyUrl = `/api/proxy/download?url=${encodeURIComponent(d.excelAttachment.url)}&filename=${encodeURIComponent(filename)}`;

            const response = await fetch(proxyUrl);
            const blob = await response.blob();
            const blobUrl = URL.createObjectURL(blob);
            const a = document.createElement("a");
            a.href = blobUrl;
            a.download = filename;
            a.click();
            URL.revokeObjectURL(blobUrl);
        } catch (err) {
            console.error("Download failed:", err);
        }
    };

    // Build the Microsoft Office Online viewer URL for in-browser Excel preview.
    // The file URL must be publicly accessible for this to work.
    const officeViewerUrl = d?.excelAttachment?.url
        ? `https://view.officeapps.live.com/op/embed.aspx?src=${encodeURIComponent(d.excelAttachment.url)}`
        : null;

    return (
        <Box padding={3}>
            <Grid container spacing={4}>

                {/* ── Key info tiles ──────────────────────────────────────────────── */}
                <Grid item xs={6} md={3}>
                    <InformationTile
                        icon={() => <BadgeOutlined color="primary" />}
                        title="Vendor Quotation Excel ID"
                        subtitle={d?.VendorQuotationExcelId || "N/A"}
                    />
                </Grid>
                <Grid item xs={6} md={3}>
                    <InformationTile
                        icon={() => <PersonOutlined color="primary" />}
                        title="Created By"
                        subtitle={d?.createdBy?.name || "N/A"}
                    />
                </Grid>
                <Grid item xs={6} md={3}>
                    <InformationTile
                        icon={() => <CalendarTodayOutlined color="primary" />}
                        title="Created At"
                        subtitle={d?.createdAt ? localizeDateTimeShort(d.createdAt) : "N/A"}
                    />
                </Grid>
                <Grid item xs={6} md={3}>
                    <InformationTile
                        icon={() => <DescriptionOutlined color="primary" />}
                        title="Title"
                        subtitle={d?.title || "N/A"}
                    />
                </Grid>

                {/* ── Excel attachment: viewer + download ─────────────────────────── */}
                {d?.excelAttachment?.url && (
                    <Grid item xs={12}>
                        <Divider sx={{ mb: 2 }} />

                        {/* Header row */}
                        <Box
                            display="flex"
                            alignItems="center"
                            justifyContent="space-between"
                            mb={2}
                        >
                            <Box display="flex" alignItems="center" gap={1}>
                                <TableChartOutlined color="primary" fontSize="small" />
                                <Typography variant="subtitle2" fontWeight={600} color="primary">
                                    Vendor Quotation Excel
                                </Typography>
                                {d.excelAttachment.name && (
                                    <Typography variant="caption" color="text.secondary">
                                        ({d.excelAttachment.name})
                                    </Typography>
                                )}
                            </Box>
                            <Button
                                variant="contained"
                                color="primary"
                                startIcon={<Download />}
                                onClick={handleDownload}
                                size="small"
                            >
                                Download Excel
                            </Button>
                        </Box>

                        {/* Embedded Excel viewer via Microsoft Office Online */}
                        {officeViewerUrl && (
                            <Box
                                component="iframe"
                                src={officeViewerUrl}
                                title="Vendor Quotation Excel"
                                sx={{
                                    width: "100%",
                                    height: "80vh",
                                    border: "1px solid",
                                    borderColor: "divider",
                                    borderRadius: 1,
                                }}
                            />
                        )}
                    </Grid>
                )}

                {/* ── In-page price grid (when parsed data is available) ───────────── */}
                {(d?.brands?.length > 0 || d?.locations?.length > 0) && (
                    <Grid item xs={12}>
                        <Divider sx={{ mb: 2 }} />
                        <Typography variant="subtitle2" fontWeight={600} color="primary" mb={2}>
                            Price Grid
                        </Typography>
                        <PriceGrid
                            brands={d?.brands ?? []}
                            locations={d?.locations ?? []}
                            entries={d?.entries ?? []}
                        />
                    </Grid>
                )}
            </Grid>
        </Box>
    );
};

export default VendorQuotationExcelBasicDetail;